import React, { useState, useEffect } from "react";
import { Card, List, Spin, Button, message, Typography, Alert, Space, Tag } from "antd";
import { CheckCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../LayoutNavbar/Navbars/DashboardNavbar";
import usePerformanceReview from "Hooks/EmployeeHooks/usePerformanceReview";

const { Title, Text } = Typography;

const EmployeePerformanceReview = () => {
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState(null);

  const { employees, loading, fetchEmployees, error: hookError } = usePerformanceReview();
  const navigate = useNavigate();

  // Handle errors from hook
  useEffect(() => {
    if (hookError) {
      setError(hookError);
    }
  }, [hookError]);

  // 🔹 Navigate to CheckReviews page
  const handleReviewClick = (employee) => {
  
    if (!employee.id) {
      message.error("Invalid employee data - missing ID");
      return;
    }

    const employeeName = `${employee.firstName} ${employee.lastName}`;

    message.success({
      content: `Opening Performance Review for ${employeeName}...`,
      duration: 2,
      className: "custom-toast-success",
      icon: <CheckCircleOutlined />,
    });

    navigate("/employeedashboard/performance-review/check-review", {
      state: {
        employeeId: employee.id, //  FIXED
        employeeName: employeeName,
        supervisorId: employee.supervisorId, // optional
        departmentId: employee.departmentId, // optional if needed
        employeeData: employee,
      },
    });
  };

  // 🔹 Handle back navigation
  const handleBack = () => {
    navigate("/employeedashboard");
  };

  // 🔹 Handle refresh
  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: "20px" }}>
        <Card
          title="Employee Performace Review"
          className="card-professional"
          extra={
            <Button onClick={handleBack} disabled={loading}>
              Back
            </Button>
          }
          style={{ borderRadius: 8 }}
        >
          {/* Error Display */}
          {error && (
            <Alert
              message="Error"
              description={error}
              type="error"
              showIcon
              action={
                <Button size="small" onClick={handleRefresh}>
                  Retry
                </Button>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Employee List Section */}
          <div>
            <Title level={4}>
              Step 1: Select Employee
              {employees && employees.length > 0 && (
                <Text type="secondary" style={{ fontSize: "14px", marginLeft: 8 }}>
                  ({employees.length} employees found)
                </Text>
              )}
            </Title>

            {/* Loading State */}
            {(loading || localLoading) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "40px",
                }}
              >
                <Spin
                  size="large"
                  tip={localLoading ? "Loading employees..." : "Loading departments..."}
                />
              </div>
            )}

            {/* No Employees */}
            {!loading && employees.length === 0 && (
              <Alert
                message="No Direct Reports Found"
                description="You don't have any employees reporting to you."
                type="info"
                showIcon
              />
            )}

            {/* Employees List */}
            {employees && employees.length > 0 && (
              <List
                dataSource={employees}
                locale={{ emptyText: "No Direct Reports found in this department" }}
                renderItem={(item) => (
                  <List.Item
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "20px 24px", // Increased padding for a premium feel
                      borderBottom: "1px solid #f1f5f9",
                      transition: "background-color 0.3s ease",
                      cursor: "pointer",
                      borderRadius: "8px", // Helps with the hover look
                      marginBottom: "4px",
                    }}
                    actions={[
                      <Button
                        className="btn-primary"
                        onClick={() => handleReviewClick(item)}
                        key="review"
                      >
                        View Detail
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Text strong style={{ fontSize: "16px" }}>
                          {item.firstName} {item.lastName}
                        </Text>
                      }
                      description={
                        <Space direction="vertical" size={2}>
                          <Text type="secondary" style={{ fontSize: "13px" }}>
                            {item.email}
                          </Text>
                          <Space>
                            {item.businessGroupName && (
                              <Tag
                                color="blue"
                                style={{
                                  border: "none",
                                  background: "#e0f2fe",
                                  color: "#0369a1",
                                  borderRadius: "4px",
                                }}
                              >
                                {item.businessGroupName}
                              </Tag>
                            )}
                            {item.currentCareerRoleTitle && (
                              <Text style={{ fontSize: "12px", color: "#0c313f", fontWeight: 500 }}>
                                • {item.currentCareerRoleTitle}
                              </Text>
                            )}
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </div>
        </Card>

        {/* Quick Actions Footer */}
        <div
          style={{
            marginTop: 24,
            padding: "20px",
            borderTop: "1px solid #f1f5f9", // Divider instead of background
            textAlign: "center",
          }}
        >
          <Text type="secondary" style={{ fontSize: "13px" }}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Need help? Contact support for performance review issues.
          </Text>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EmployeePerformanceReview;
